import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, NavParams } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  CUSTOM_FIELD_TYPE_MULTI_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
  CUSTOM_FIELD_TYPE_SHORT_ANSWER,
  CUSTOM_FIELD_TYPE_SINGLE_SELECT,
  CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_USER_SELECT,
  STATUS_COLUMN_FORMAT_MAP,
} from 'src/app/constants';
import { generateCustomFieldsArray } from 'src/app/utils/utils';
import { environment } from 'src/environments/environment';
import { CustomField, SubStatus } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { AvatarModule } from '../avatar/avatar.module';
import { ChannelSelectorModule } from '../channel-selector/channel-selector.module';
import { CustomFieldsModule } from '../custom-fields/custom-fields.module';
import { ResponseInputModule } from '../custom-fields/response-input/response-input.module';

type SubStatusWithIcon = SubStatus & { icon?: string; iconClass?: string };

@Component({
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    AvatarModule,
    ResponseInputModule,
  ],
  selector: 'app-edit-custom-field',
  templateUrl: './edit-custom-field.component.html',
  styleUrls: ['./edit-custom-field.component.scss'],
})
export class EditCustomFieldComponent implements OnInit {
  isDev: boolean;
  isLoading = false;
  id?: string;
  projectId: string;
  field: CustomField = {
    label: '',
    description: '',
    type: CUSTOM_FIELD_TYPE_SHORT_ANSWER,
    required: false,
    public: true,
    id: uuidv4(),
    conditions: {
      type: 'simple',
      simple: {},
    },
    prefillOnSubStatusId: null,
    prefillPrompt: null,
  };

  availableSubStatuses: SubStatusWithIcon[] = [];
  selectedSubStatusDisplay: SubStatusWithIcon | null = null;

  constructor(
    navParams: NavParams,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private searchablePopoverService: SearchablePopoverService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
    this.isDev = environment.environment === 'dev';
  }

  async ngOnInit() {
    await this.loadSubStatuses();
    if (this.id) {
      await this.loadCustomField();
    }
  }

  async loadSubStatuses() {
    if (!this.projectId) {
      console.warn('Project ID not available, cannot load sub-statuses.');
      return;
    }
    try {
      const result = await firstValueFrom(
        this.apiService.post<{ data: SubStatus[] }>('/sub-statuses/list', {
          where: [{ field: 'projectId', operator: '==', value: this.projectId }],
          order: [
            ['typeOrd', 'ASC'],
            ['position', 'ASC'],
          ],
          limit: 1000,
        }),
      );
      this.availableSubStatuses = (result.data || []).map((subStatus) => {
        const type = subStatus.key.split('__')[0];
        const format = STATUS_COLUMN_FORMAT_MAP[type];
        return {
          ...subStatus,
          icon: format?.icon,
          iconClass: format?.iconClass,
        };
      });
    } catch (error) {
      console.error('Error loading sub-statuses:', error);
      this.msgSrvc.showError('Failed to load status options.');
      this.availableSubStatuses = [];
    }
    this.updateSelectedSubStatusDisplay();
  }

  async loadCustomField() {
    try {
      const customField = await this.customFieldsService.getById(this.id);
      this.field = customField;
      this.updateSelectedSubStatusDisplay();
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  updateSelectedSubStatusDisplay() {
    if (this.field.prefillOnSubStatusId) {
      this.selectedSubStatusDisplay =
        this.availableSubStatuses.find((s) => s.id === this.field.prefillOnSubStatusId) || null;
    } else {
      this.selectedSubStatusDisplay = null;
    }
  }

  showSubStatusPopover(event: Event) {
    const popoverItems = [
      { id: null, title: 'None', icon: undefined, iconClass: undefined, object: null },
      ...this.availableSubStatuses.map((subStatus) => ({
        id: subStatus.id,
        title: `${subStatus.name} (${subStatus.type})`,
        icon: subStatus.icon,
        iconClass: subStatus.iconClass,
        object: subStatus,
      })),
    ];

    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.field.prefillOnSubStatusId ?? undefined,
      showSearch: false,
      componentView: 'SubStatusSelect',
      initialItems: popoverItems,
      callback: (id, _name, object) => {
        this.field.prefillOnSubStatusId = id;
        this.selectedSubStatusDisplay = object;
      },
      loadData: async () => {
        return popoverItems;
      },
    });
  }

  dismiss(customField?: CustomField) {
    this.modalCtrl.dismiss({ customField });
  }

  async submit() {
    const canSetAllowedValues = [
      CUSTOM_FIELD_TYPE_USER_SELECT,
      CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
    ].includes(this.field.type);

    const canSetOptions = [CUSTOM_FIELD_TYPE_SINGLE_SELECT, CUSTOM_FIELD_TYPE_MULTI_SELECT].includes(this.field.type);

    const cleanedField = {
      ...this.field,
      label: this.field.label?.trim() || `New ${this.field.type} field`,
      options: canSetOptions && !this.field.externalSelect ? this.field.options : null,
      allowedValues: canSetAllowedValues ? this.field.allowedValues : null,
      projectId: this.projectId,
      prefillOnSubStatusId: this.field.prefillOnSubStatusId,
      prefillPrompt: this.field.prefillPrompt,
    };
    try {
      if (this.id) {
        const result = await this.customFieldsService.updatePartial(this.id, cleanedField);
        await this.msgSrvc.show('Custom field updated!');
        this.dismiss(result);
      } else {
        const result = await this.customFieldsService.create(cleanedField);
        await this.msgSrvc.show('Custom field added!');
        this.dismiss(result);
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete custom field?',
      subHeader: `Please remove this field from any ticket types before deleting. Deletion can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.customFieldsService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate custom field?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.field.id = undefined;
            this.id = undefined;
            setTimeout(() => {
              this.field.label = `Copy of ${this.field.label}`;
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }

  getPreviewField() {
    return generateCustomFieldsArray([this.field], [], {});
  }
}
